/**
 * Calendar maths, shared by the Worker and the SPA.
 *
 * MD labels are computed on both sides — the server stores them with a
 * microcycle, the client renders them on a calendar — so there is exactly one
 * implementation rather than two that agree until they do not.
 *
 * Everything works in UTC on 'YYYY-MM-DD' strings. A fixture is a date, not an
 * instant: a match on 4 October is on 4 October in Casablanca and in Paris, and
 * building the grid from local Date objects would shift it across a month
 * boundary for anyone the wrong side of midnight.
 *
 * Weeks start on Monday by default — a football microcycle runs Monday to
 * Sunday, and MD-3 means nothing against a Sunday-first grid.
 */

export type WeekStart = 0 | 1; // 0 = Sunday, 1 = Monday

export function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function fromISODate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

export function todayISO(): string {
  return toISODate(new Date());
}

export function startOfMonth(year: number, month: number): Date {
  return new Date(Date.UTC(year, month, 1));
}

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(Date.UTC(year, month + delta, 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
}

export function addDays(iso: string, days: number): string {
  const d = fromISODate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toISODate(d);
}

/** Days between two dates, positive when `to` is later. */
export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round(
    (fromISODate(toIso).getTime() - fromISODate(fromIso).getTime()) / 86_400_000,
  );
}

/**
 * Six weeks of dates covering the month, padded from neighbouring months so the
 * grid never changes height as the coach pages through — a shifting layout is
 * the difference between scanning a season and re-reading it every month.
 */
export function monthGrid(
  year: number,
  month: number,
  weekStart: WeekStart = 1,
): string[][] {
  const first = startOfMonth(year, month);
  const offset = (first.getUTCDay() - weekStart + 7) % 7;

  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - offset);

  const weeks: string[][] = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week: string[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(toISODate(cursor));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

export function isInMonth(iso: string, year: number, month: number): boolean {
  const d = fromISODate(iso);
  return d.getUTCFullYear() === year && d.getUTCMonth() === month;
}

/**
 * Relative match-day label for a training date: MD-3, MD, MD+1.
 *
 * The nearest match wins, with ties going to the upcoming one, because a week is
 * planned towards the next fixture rather than away from the last. Returns null
 * when no fixture is close enough for the label to mean anything.
 */
export function matchDayLabel(
  iso: string,
  matchDates: readonly string[],
  window = 7,
): string | null {
  let best: { offset: number; abs: number } | null = null;
  for (const match of matchDates) {
    const offset = daysBetween(match, iso);
    const abs = Math.abs(offset);
    if (abs > window) continue;
    if (!best || abs < best.abs || (abs === best.abs && offset < 0)) {
      best = { offset, abs };
    }
  }
  if (!best) return null;
  if (best.offset === 0) return 'MD';
  return best.offset < 0 ? `MD${best.offset}` : `MD+${best.offset}`;
}
